import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    IOcbAzureStorageRemotePlatform,
    IOcbFTPRemotePlatform,
    IOcbHTTPRemotePlatform,
    IOcbSFTPRemotePlatform,
    IOcbSharePointRemotePlatform,
    OcbAzureStorageAuthenticationModes,
    OcbAzureStorageServices,
    OcbSftpAuthenticationModes,
    OcbSharePointAuthenticationModes,
    OcbSpPlatforms,
    RemotePlatformsService
} from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { Observable, forkJoin, from as fromPromise } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';
import {
    AzureStoragePopupComponent,
    FtpPopupComponent,
    HttpPopupComponent,
    LocalAgentCheckConnectionComponent,
    SftpPopupComponent,
    SharePointPopupComponent
} from '../../shared';

@Component({
    selector: 'app-remote-platforms',
    templateUrl: './remote-platforms.component.html',
    styleUrls: ['./remote-platforms.component.scss'],
})
export class RemotePlatformsComponent implements AfterViewInit {

    @ViewChild('azureStorageGrid')
    public azureStorageGrid: DxDataGridComponent;
    @ViewChild('ftpGrid')
    public ftpGrid: DxDataGridComponent;
    @ViewChild('httpGrid')
    public httpGrid: DxDataGridComponent;
    @ViewChild('sftpGrid')
    public sftpGrid: DxDataGridComponent;
    @ViewChild('spGrid')
    public spGrid: DxDataGridComponent;
    @ViewChild('checkConnectionPopup')
    checkConnectionPopup: LocalAgentCheckConnectionComponent;

    @ViewChild('azureStoragePopup')
    azureStoragePopup: AzureStoragePopupComponent;
    @ViewChild('ftpPopup')
    ftpPopup: FtpPopupComponent;
    @ViewChild('httpPopup')
    httpPopup: HttpPopupComponent;
    @ViewChild('sftpPopup')
    sftpPopup: SftpPopupComponent;
    @ViewChild('sharepointPopup')
    sharepointPopup: SharePointPopupComponent;

    public activeTab: 'ftp' | 'http' | 'sftp' | 'sharepoint' | 'azureStorage' = 'ftp';
    public isLoadPanelVisible: boolean = false;
    public selectedType: string = null;

    public azureStorageDataSource: IOcbAzureStorageRemotePlatform[] = null;
    public ftpDataSource: IOcbFTPRemotePlatform[] = null;
    public httpDataSource: IOcbHTTPRemotePlatform[] = null;
    public sftpDataSource: IOcbSFTPRemotePlatform[] = null;
    public spDataSource: IOcbSharePointRemotePlatform[] = null;

    private azureStorageAuthenticationModes: any[];
    private currentEnvironmentId: string = '';
    private sftpAuthenticationModes: any[];
    private sharePointAuthenticationModes: any[];

    private executeDelete(type: string, id: string): Observable<void> {
        switch (type) {
            case 'azureStorage': return this.remotePlatformsService.deleteAzureStorageRemotePlatform(id);
            case 'ftp': return this.remotePlatformsService.deleteFTPRemotePlatform(id);
            case 'http': return this.remotePlatformsService.deleteHTTPRemotePlatform(id);
            case 'sftp': return this.remotePlatformsService.deleteSFTPRemotePlatform(id);
            case 'sp': return this.remotePlatformsService.deleteSharePointRemotePlatform(id);
        }
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }

    private loadData(): void {
        this.currentEnvironmentId = this.route.snapshot.params.id;
        this.loadFtpDataSource();
        this.loadHttpDataSource();
        this.loadSftpDataSource();
        this.loadSharePointDataSource();
        this.loadAzureStorageDataSource();
    }

    private loadAzureStorageDataSource() {
        if (this.activeTab === 'azureStorage') {
            this.showLoading$(this.azureStorageGrid, '_commons.loading')
                .pipe(
                    mergeMap(_ => this.remotePlatformsService.getAzureStorageRemotePlatforms(this.currentEnvironmentId)),
                    finalize(() => this.azureStorageGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.azureStorageDataSource = res,
                    error: (err) => this.handleError(err)
                });
        } else {
            this.remotePlatformsService.getAzureStorageRemotePlatforms(this.currentEnvironmentId).subscribe({
                next: (res) => this.azureStorageDataSource = res,
                error: (err) => this.handleError(err)
            });
        }
    }

    private loadFtpDataSource() {
        if (this.activeTab === 'ftp') {
            this.showLoading$(this.ftpGrid, '_commons.loading')
                .pipe(
                    mergeMap(_ => this.remotePlatformsService.getFTPRemotePlatforms(this.currentEnvironmentId)),
                    finalize(() => this.ftpGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.ftpDataSource = res,
                    error: (err) => this.handleError(err)
                });
        } else {
            this.remotePlatformsService.getFTPRemotePlatforms(this.currentEnvironmentId).subscribe({
                next: (res) => this.ftpDataSource = res,
                error: (err) => this.handleError(err)
            });
        }
    }

    private loadHttpDataSource() {
        if (this.activeTab === 'http') {
            this.showLoading$(this.httpGrid, '_commons.loading')
                .pipe(
                    mergeMap(_ => this.remotePlatformsService.getHTTPRemotePlatforms(this.currentEnvironmentId)),
                    finalize(() => this.httpGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.httpDataSource = res,
                    error: (err) => this.handleError(err)
                });
        } else {
            this.remotePlatformsService.getHTTPRemotePlatforms(this.currentEnvironmentId).subscribe({
                next: (res) => this.httpDataSource = res,
                error: (err) => this.handleError(err)
            });
        }
    }

    private loadSftpDataSource() {
        if (this.activeTab === 'sftp') {
            this.showLoading$(this.sftpGrid, '_commons.loading')
                .pipe(
                    mergeMap(_ => this.remotePlatformsService.getSFTPRemotePlatforms(this.currentEnvironmentId)),
                    finalize(() => this.sftpGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.sftpDataSource = res,
                    error: (err) => this.handleError(err)
                });
        } else {
            this.remotePlatformsService.getSFTPRemotePlatforms(this.currentEnvironmentId).subscribe({
                next: (res) => this.sftpDataSource = res,
                error: (err) => this.handleError(err)
            });
        }
    }

    private loadSharePointDataSource() {
        if (this.activeTab === 'sharepoint') {
            this.showLoading$(this.spGrid, '_commons.loading')
                .pipe(
                    mergeMap(_ => this.remotePlatformsService.getSharePointRemotePlatforms(this.currentEnvironmentId)),
                    finalize(() => this.spGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.spDataSource = res,
                    error: (err) => this.handleError(err)
                });
        } else {
            this.remotePlatformsService.getSharePointRemotePlatforms(this.currentEnvironmentId).subscribe({
                next: (res) => this.spDataSource = res,
                error: (err) => this.handleError(err)
            });
        }
    }

    private reloadDataSource() {
        switch (this.activeTab) {
            case 'azureStorage': this.loadAzureStorageDataSource(); break;
            case 'ftp': this.loadFtpDataSource(); break;
            case 'http': this.loadHttpDataSource(); break;
            case 'sftp': this.loadSftpDataSource(); break;
            case 'sharepoint': this.loadSharePointDataSource(); break;
        }
    }

    private showCheckConnectionPopup(remotePlatformId: string, sftpEngine?: string) {
        this.checkConnectionPopup.remotePlatformId = remotePlatformId;
        if (sftpEngine) {
            this.checkConnectionPopup.sftpEngine = sftpEngine;
        }
        this.checkConnectionPopup.visible = true;
    }

    private showLoading$(datagrid: DxDataGridComponent, messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => datagrid.instance.beginCustomLoading(`${message}...`)),
        );
    }


    constructor(
        private route: ActivatedRoute,
        private remotePlatformsService: RemotePlatformsService,
        private translateService: TranslateService,
        private router: Router) {
        this.getAzureStorageAuthenticationMode = this.getAzureStorageAuthenticationMode.bind(this);
        this.getSftpAuthenticationMode = this.getSftpAuthenticationMode.bind(this);
        this.getSharePointAuthenticationMode = this.getSharePointAuthenticationMode.bind(this);
        forkJoin([
            this.translateService.get('azureStorageAuthenticationModes'),
            this.translateService.get('sftpAuthenticationModes'),
            this.translateService.get('sharePointAuthenticationModes')
        ]).subscribe({
            next: (messages) => {
                this.azureStorageAuthenticationModes = messages[0];
                this.sftpAuthenticationModes = messages[1];
                this.sharePointAuthenticationModes = messages[2];
            },
            error: (err) => this.handleError(err)
        });
    }

    public ngAfterViewInit() {
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.loadData();
        });
    }


    public onContextMenuPreparing(e, type: string) {
        if (e.row && e.row.rowType === 'data' && e.row.data?.id) {
            forkJoin([
                this.translateService.get('_commons.seeDetails'),
                this.translateService.get('_commons.edit'),
                this.translateService.get('_commons.delete'),
                this.translateService.get('_commons.connectionCheck'),
                this.translateService.get('_commons.seeLogs')
            ]).subscribe({
                next: (messages) => {
                    e.items = [
                        { text: messages[0], icon: 'description', onItemClick: (ev) => this.displayRemotePlatform(ev, type, e.row.data) },
                        { text: messages[1], icon: 'edit', beginGroup: true, onItemClick: (ev) => this.editRemotePlatform(ev, type, e.row.data) },
                        { text: messages[2], icon: 'trash', onItemClick: (ev) => this.deleteRemotePlatform(ev, type, e.row.data.id) },
                        {
                            text: messages[3],
                            icon: 'check',
                            beginGroup: true,
                            onItemClick: (ev) => {
                                if (e.row.data.isSFTP)
                                    this.showCheckConnectionPopup(e.row.data.id, e.row.data.useIpWorksEngine ? 'ipworks' : 'secureblackbox');
                                else
                                    this.showCheckConnectionPopup(e.row.data.id);
                            }
                        },
                        { text: messages[4], icon: 'clock', beginGroup: true, onItemClick: (ev) => this.router.navigateByUrl(`/${this.currentEnvironmentId}/logs?remotePlatform=${e.row.data.id}`) }
                    ];
                },
                error: (err) => this.handleError(err)
            });
        }
    }

    public onRowPrepared(e): void {
        if (e.data != null) {
            if (e.data.isDisabled === true) { e.rowElement.bgColor = '#FFCECE'; }
        }
    }


    public deleteRemotePlatform(e, type: string, id: string): void {
        e.event.stopPropagation();
        forkJoin([
            this.translateService.get('remotePlatforms.msgDeleteConfirm'),
            this.translateService.get('remotePlatforms.msgDeleteConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.executeDelete(type, id)),
                // Reload the email template
                mergeMap(_ => this.translateService.get('remotePlatforms.msgFileTransferDeleteComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.reloadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    public displayRemotePlatform(e, type: string, remotePlatform?: any): void {
        switch (type) {
            case 'ftp':
                this.ftpPopup.platform = e.data ?? (remotePlatform as IOcbFTPRemotePlatform);
                this.ftpPopup.readOnly = true;
                this.ftpPopup.visible = true;
                break;
            case 'http':
                this.httpPopup.platform = e.data ?? (remotePlatform as IOcbHTTPRemotePlatform);
                this.httpPopup.readOnly = true;
                this.httpPopup.visible = true;
                break;
            case 'sftp':
                this.sftpPopup.platform = e.data ?? (remotePlatform as IOcbSFTPRemotePlatform);
                this.sftpPopup.readOnly = true;
                this.sftpPopup.visible = true;
                break;
            case 'sp':
                this.sharepointPopup.platform = e.data ?? (remotePlatform as IOcbSharePointRemotePlatform);
                this.sharepointPopup.readOnly = true;
                this.sharepointPopup.visible = true;
                break;
            case 'azureStorage':
                this.azureStoragePopup.platform = e.data ?? (remotePlatform as IOcbAzureStorageRemotePlatform);
                this.azureStoragePopup.readOnly = true;
                this.azureStoragePopup.visible = true;
                break;
        }
        this.selectedType = type;
    }

    public editRemotePlatform(e, type: string, remotePlatform: any): void {
        e.event.stopPropagation();
        switch (type) {
            case 'ftp':
                this.ftpPopup.platform = remotePlatform as IOcbFTPRemotePlatform;
                this.ftpPopup.readOnly = false;
                this.ftpPopup.visible = true;
                break;
            case 'http':
                this.httpPopup.platform = remotePlatform as IOcbHTTPRemotePlatform;
                this.httpPopup.readOnly = false;
                this.httpPopup.visible = true;
                break;
            case 'sftp':
                this.sftpPopup.platform = remotePlatform as IOcbSFTPRemotePlatform;
                this.sftpPopup.readOnly = false;
                this.sftpPopup.visible = true;
                break;
            case 'sp':
                this.sharepointPopup.platform = remotePlatform as IOcbSharePointRemotePlatform;
                this.sharepointPopup.readOnly = false;
                this.sharepointPopup.visible = true;
                break;
            case 'azureStorage':
                this.azureStoragePopup.platform = remotePlatform as IOcbAzureStorageRemotePlatform;
                this.azureStoragePopup.readOnly = false;
                this.azureStoragePopup.visible = true;
                break;
        }
        this.selectedType = type;
    }

    public getAzureStorageAuthenticationMode(rowData) {
        return this.azureStorageAuthenticationModes[OcbAzureStorageAuthenticationModes[rowData.authenticationMode]];
    }

    public getAzureStorageService(rowData) { return OcbAzureStorageServices[rowData.serviceType]; }

    public getSftpAuthenticationMode(rowData) {
        return this.sftpAuthenticationModes[OcbSftpAuthenticationModes[rowData.authenticationMode]];
    }

    public getSharePointAuthenticationMode(rowData) {
        return this.sharePointAuthenticationModes[OcbSharePointAuthenticationModes[rowData.authenticationMode]];
    }

    public getSharePointPlatform(rowData) { return OcbSpPlatforms[rowData.platform]; }

    public remotePlatformPopupClosed(hasChanges: boolean) { this.reloadDataSource(); }

    public newRemotePlatform(type: string): void {
        switch (type) {
            case 'ftp':
                this.ftpPopup.readOnly = false;
                this.ftpPopup.visible = true;
                break;
            case 'http':
                this.httpPopup.readOnly = false;
                this.httpPopup.visible = true;
                break;
            case 'sftp':
                this.sftpPopup.readOnly = false;
                this.sftpPopup.visible = true;
                break;
            case 'sp':
                this.sharepointPopup.readOnly = false;
                this.sharepointPopup.visible = true;
                break;
            case 'azureStorage':
                this.azureStoragePopup.readOnly = false;
                this.azureStoragePopup.visible = true;
                break;
        }
        this.selectedType = type;
    }

}
